import { uuid4 } from '../../../../../shared/utils/uuid';

export interface QueueEvent<T> {
    id: string;
    event: T;
}

export function waitExponentialBackoff(attempts: number) {
    return new Promise(r => setTimeout(
        r,
        (attempts ** 2) * 1000 * (1 + Math.floor(Math.random() * 6) / 100),
    ));
}

export interface QueueOptions {
    maxRetries: number;
}

export class Queue<T> {
    private readonly items: QueueEvent<T>[];

    private readonly processor: (item: T) => Promise<any>;

    private intervalId = null;

    private processingCount = 0;

    private options: QueueOptions;

    constructor(options: QueueOptions, processor: (item: T) => Promise<any>) {
        this.items = [];
        this.processor = processor;
        this.options = options;
    }

    add(event: T) {
        this.items.push({ id: uuid4(), event });
    }

    start() {
        if (!this.intervalId) {
            this.intervalId = setInterval(this.processTick.bind(this), 100);
        }

        return () => clearInterval(this.intervalId);
    }

    private async processTick() {
        if (this.processingCount > 0 || this.items.length === 0) {
            return;
        }

        const event = this.items[0];

        this.processingCount += 1;
        try {
            await this.processEvent(event);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            this.items.splice(0, 1);
            this.processingCount -= 1;
        }
    }

    private async processEvent(event: QueueEvent<T>, attempts = 0) {
        if (attempts > this.options.maxRetries) {
            throw new Error('max retries exceeded');
        }

        try {
            await this.processor(event.event);
        } catch (error) {
            const nextAttempts = attempts + 1;
            await waitExponentialBackoff(nextAttempts);
            await this.processEvent(event, nextAttempts);
        }
    }
}
